import React, { lazy, Suspense } from "react";
import { BrowserRouter, Redirect, Switch } from "react-router-dom";

import { makeServer } from "server/server";

import Amplify from "aws-amplify";

import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import Loader from "./components/Loader/Loader";
import { AdminProvider } from "providers/AdminProvider/AdminProvider";

import AdminRoute from "./routes/AdminRoute";
import PortalRouter from "routes/PortalRouter";
import ResourceRoute from "./routes/ResourceRoute";

import * as routes from "routes";

if (process.env.REACT_APP_USE_MOCK_SERVER) {
  makeServer({ environment: "development" });
}

const config = {
  Auth: {
    region: "eu-west-1",
    userPoolId: process.env.REACT_APP_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
    authenticationFlowType: "USER_PASSWORD_AUTH",
  },
};

Amplify.configure(config);

const Page404 = lazy(() => import("./pages/Page404/Page404"));

const Login = lazy(() => import("./pages/Login/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword/ForgotPassword"));

const Home = lazy(() => import("./pages/Home/Home"));
const Search = lazy(() => import("./pages/Search/Search"));

const CourseList = lazy(() => import("./pages/CourseList/CourseList"));
const Course = lazy(() => import("./pages/Course/Course"));
const CourseSession = lazy(() => import("./pages/CourseSession/CourseSession"));

const PortalList = lazy(() => import("./pages/PortalList/PortalList"));
const CreatePortal = lazy(() => import("./pages/CreatePortal/CreatePortal"));

const Administrators = lazy(() => import("./pages/Administrators/Administrators"));
const Administrator = lazy(() => import("./pages/Administrator/Administrator"));

const CustomItems = lazy(() => import("./pages/CustomisableItems/CustomisableItems"));

const LMSClientList = lazy(() => import("./pages/LMSClients/LMSClientList"));
const LMSClient = lazy(() => import("./pages/LMSClient/LMSClient"));

const App = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loader />}>
      <BrowserRouter>
        <AdminProvider>
          <Switch>
            <AdminRoute exact path={routes.LOGIN}>
              <Login />
            </AdminRoute>

            <AdminRoute exact path={routes.FORGOT_PASSWORD}>
              <ForgotPassword />
            </AdminRoute>

            <AdminRoute isPrivate exact path={routes.SEARCH}>
              <Search />
            </AdminRoute>

            <AdminRoute isPrivate exact path={routes.PORTALS}>
              <PortalList />
            </AdminRoute>

            <AdminRoute isPrivate exact isGlobalAdmin path={routes.COURSES}>
              <CourseList />
            </AdminRoute>

            <AdminRoute isPrivate exact isGlobalAdmin path={routes.COURSE_ID}>
              <Course />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin path={routes.COURSE_SESSION}>
              <CourseSession />
            </AdminRoute>

            <AdminRoute isPrivate exact path={routes.CREATE_PORTAL}>
              <CreatePortal />
            </AdminRoute>

            <AdminRoute isPrivate path={routes.PORTAL_ID}>
              <PortalRouter />
            </AdminRoute>

            <AdminRoute isPrivate exact isGlobalAdmin path={routes.ADMINISTRATORS}>
              <Administrators />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin path={routes.ADMINISTRATOR_ID}>
              <Administrator />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin path={routes.RESOURCES}>
              <ResourceRoute />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin path={routes.CONFIGURE_CUSTOMITEMS}>
              <CustomItems />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin exact path={routes.LMS_CLIENTS}>
              <LMSClientList />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin exact path={routes.LMS_ADD_CLIENT}>
              <LMSClient />
            </AdminRoute>

            <AdminRoute isPrivate isGlobalAdmin exact path={routes.LMS_CLIENT}>
              <LMSClient />
            </AdminRoute>

            <AdminRoute isPrivate exact path={routes.DASHBOARD}>
              <Home />
            </AdminRoute>

            <AdminRoute isPrivate exact path={routes.OLD_LOGIN}>
              <Redirect to={routes.LOGIN} />
            </AdminRoute>

            <AdminRoute isPrivate path={"*"}>
              <Page404 />
            </AdminRoute>
          </Switch>
        </AdminProvider>
      </BrowserRouter>
    </Suspense>
  </ErrorBoundary>
);

export default App;
